import { useEffect, useRef, useState } from 'react';
import { ResponsiveContainer } from 'recharts';
import type { ChartConfig } from './chart-utils';
import { chartConfigToCSS } from './chart-utils';

interface ChartContainerProps {
  config: ChartConfig;
  /** Entweder ein fertiges Chart-Element ODER eine Funktion, die die aktuell
   *  gemessene Containerbreite bekommt (fuer responsive Achsen). */
  children: React.ReactNode | ((width: number) => React.ReactElement);
  className?: string;
  height?: number;
  /** Zugaengliches Label fuer Screenreader (beschreibt den Chart-Inhalt) */
  ariaLabel?: string;
}

/**
 * ChartContainer - Wrapper fuer alle Recharts-Charts.
 * Setzt CSS-Variablen aus der ChartConfig und sorgt fuer responsive Darstellung.
 *
 * Wird `children` als Funktion uebergeben, bekommt sie die per ResizeObserver
 * gemessene Containerbreite — so koennen die Charts ihre Achsen-Labels auf
 * schmalen Viewports drehen, statt sie ueberlappen zu lassen.
 */
export function ChartContainer({
  config,
  children,
  className = '',
  height = 350,
  ariaLabel,
}: ChartContainerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(e.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const content =
    typeof children === 'function'
      ? (children as (w: number) => React.ReactElement)(width)
      : children;

  return (
    <div
      ref={ref}
      className={`chart-container ${className}`}
      style={chartConfigToCSS(config)}
      role="img"
      aria-label={ariaLabel ?? 'Diagramm'}
    >
      <ResponsiveContainer width="100%" height={height}>
        {content as React.ReactElement}
      </ResponsiveContainer>
    </div>
  );
}
